import { useEffect, useMemo } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { get, isObject, isArray } from "lodash"
import { Creators as GraphqlCreators } from "model/graphql"
import { Creators as DataCreators } from "model/data"
import { LOADING, OK, ResourceResult } from "helpers/data-statuses"
import { ReduxDataResult, ReduxState } from "model/state"

// Once the PR is approved, I'll bring these types over to resource.ts
export interface QueryDefinitionGraphQL {
  query: string
  id: string
  variables?: object
}

export interface QueryDefinitionRest {
  query?: undefined
  id?: string
  url: string
  payload?: object
}

export interface LegacyQueryDefinition<T extends object> {
  resource: string
  url: (params: T) => QueryDefinitionGraphQL | QueryDefinitionRest | string | null
}

export interface QueryDefinition {
  resource: string
  url: () => QueryDefinitionGraphQL | QueryDefinitionRest | string | null
}

export interface ResourceQueryDefinitionRest {
  resource: string
  id: string | null
  url: string | null
  payload?: object

  // Only on GraphQL
  isGraphQl?: undefined
  query?: undefined
  variables?: undefined
}

export interface ResourceQueryDefinitionGraphQL {
  resource: string
  id: string
  isGraphQl: true
  query: string
  variables?: object

  // Only on Rest
  url?: undefined
  payload?: undefined
}

function getQueryInfo(definition: QueryDefinition): ResourceQueryDefinitionGraphQL | ResourceQueryDefinitionRest {
  const resource = definition.resource
  const definitionUrl = definition.url()

  // Get request with url as ID
  if (!isObject(definitionUrl)) {
    return { resource, url: definitionUrl, id: definitionUrl }
  }

  if (definitionUrl.query !== undefined) {
    // GraphQL query
    const { query, id, variables } = definitionUrl
    return {
      resource,
      isGraphQl: true,
      id: id || query,
      query,
      variables,
    }
  } else {
    const { id, url, payload } = definitionUrl
    return {
      resource,
      id: id || url,
      url,
      payload,
    }
  }
}

export default function useResource<R>(definition: QueryDefinition, path: string | string[]): ResourceResult<R> {
  const { resource, isGraphQl, url, id, query, variables, payload } = useMemo(() => getQueryInfo(definition), [definition])

  const [result, account] = useSelector((state: ReduxState): [ReduxDataResult<R> | undefined, string] => [
    state.global.data[resource] as ReduxDataResult<R> | undefined,
    state.global.selected.account,
  ], shallowEqual)
  const dispatch = useDispatch()

  useEffect(() => {
    if (id == null) return
    if (!result || result.id !== id) {
      if (isGraphQl) {
        dispatch(GraphqlCreators.load(resource, id, query, variables, account))
      } else {
        dispatch(DataCreators.load(resource, id, url, payload))
      }
    }
  }, [isGraphQl, resource, url, id, query, variables, account, dispatch, result, payload])

  if (!id) {
    return [null, OK]
  }

  if (!result || result.id !== id) {
    return [null, LOADING]
  }

  if (result.status === OK) {
    if (isGraphQl) {
      return [get(result, ["data", ...(isArray(path) ? path : [path])]) as R, OK]
    } else {
      return [result.data, OK]
    }
  }

  return [null, result.status]
}
